import apiClient from './client';
import { Lesson, GenerateLessonRequest, UpdateLessonRequest, PublicLessonRef, CostEstimate, LessonCompletionResult, LessonWordResult } from '../types/lesson';

export interface ShareTokenResponse {
    share_token: string;
    share_url: string;
}

// GENERATE_TIMEOUT_MS must exceed the backend's own provider-call timeout (5 min —
// lessonGenerationTimeout in weavewords-api's anthropic_lesson_generator.go). Otherwise the
// browser aborts the fetch (and, via the request's context, the in-flight OpenAI/Anthropic call
// itself) before the backend's own timeout ever gets a chance to fire, turning a
// slow-but-successful generation — a 60-minute lesson can take several minutes — into a
// guaranteed failure.
const GENERATE_TIMEOUT_MS = 320_000;

export const lessonService = {
    generate: async (req: GenerateLessonRequest): Promise<Lesson> => {
        const response = await apiClient.post('/api/v1/lessons/generate', req, { timeoutMs: GENERATE_TIMEOUT_MS });
        return response.data as Lesson;
    },

    estimateCost: async (req: Partial<GenerateLessonRequest> & { duration_minutes: GenerateLessonRequest['duration_minutes'] }): Promise<CostEstimate> => {
        const response = await apiClient.post('/api/v1/lessons/estimate-cost', req);
        return response.data as CostEstimate;
    },

    list: async (limit = 50, offset = 0): Promise<Lesson[]> => {
        const response = await apiClient.get('/api/v1/lessons', { params: { limit, offset } });
        return response.data as Lesson[];
    },

    getById: async (id: number): Promise<Lesson> => {
        const response = await apiClient.get(`/api/v1/lessons/${id}`);
        return response.data as Lesson;
    },

    update: async (id: number, req: UpdateLessonRequest): Promise<Lesson> => {
        const response = await apiClient.put(`/api/v1/lessons/${id}`, req);
        return response.data as Lesson;
    },

    delete: async (id: number): Promise<void> => {
        await apiClient.delete(`/api/v1/lessons/${id}`);
    },

    // Awards the lesson's XP and SM-2 reviews server-side — once per lesson, however many times
    // it's finished (retry, Restart, another device). XP is computed by the server from results.
    complete: async (id: number, results: LessonWordResult[], fallbackLanguage?: string): Promise<LessonCompletionResult> => {
        const response = await apiClient.post(`/api/v1/lessons/${id}/complete`, { results, fallback_language: fallbackLanguage });
        return response.data as LessonCompletionResult;
    },

    getShareLink: async (id: number): Promise<ShareTokenResponse> => {
        const response = await apiClient.post(`/api/v1/lessons/${id}/share`);
        return response.data as ShareTokenResponse;
    },

    revokeShareLink: async (id: number): Promise<void> => {
        await apiClient.delete(`/api/v1/lessons/${id}/share`);
    },

    getPublicByToken: async (token: string): Promise<PublicLessonRef> => {
        const response = await apiClient.getPublic(`/api/lessons/share/${token}`);
        return response.data as PublicLessonRef;
    },
};
